import { useEffect, useRef, useState } from 'react';

const comparisonRows = [
  {
    alone: 'No physical showroom to close contracts',
    hub: '4,000 SQF Showroom — the most powerful closing environment in the market',
  },
  {
    alone: 'Losing contracts on price — competing with anyone',
    hub: '$30M infrastructure that justifies and closes $100k+ contracts',
  },
  {
    alone: 'Zero brand authority — invisible to high-value clients',
    hub: 'Elite branding with the Barkfy Seal — instant authority',
  },
  {
    alone: 'Client can\'t pay upfront = lost contract',
    hub: 'Bank financing for the client — you close, they finance',
  },
  {
    alone: '100% dependent on referrals to survive',
    hub: 'Active commercial team generating leads and closing for you',
  },
];

export default function IrresistibleOfferSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const fadeIn = (delay = '0ms') => ({
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(28px)',
    transition: `opacity 0.7s ease ${delay}, transform 0.7s ease ${delay}`,
  });

  return (
    <section
      id="offer"
      ref={ref}
      style={{ background: '#FFFFFF', padding: '120px 60px' }}
      className="px-4 md:px-[60px]"
    >
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="text-center mb-20" style={fadeIn('0ms')}>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div style={{ width: '32px', height: '1px', background: '#DC5014' }} />
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '11px',
                color: '#DC5014',
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
              }}
            >
              THE DECISION
            </span>
            <div style={{ width: '32px', height: '1px', background: '#DC5014' }} />
          </div>

          <h2
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(34px, 5vw, 60px)',
              fontWeight: 700,
              color: '#143C3C',
              lineHeight: 1.05,
              marginBottom: '20px',
            }}
          >
            Stop Chasing Referrals.
            <br />
            <span style={{ color: '#DC5014', fontStyle: 'italic' }}>Start Leading a Corporation.</span>
          </h2>

          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 300,
              fontSize: '17px',
              color: 'rgba(20,60,60,0.55)',
              maxWidth: '520px',
              margin: '0 auto',
              lineHeight: 1.8,
            }}
          >
            The difference between a contractor who survives and a corporation that dominates the market is the infrastructure behind it.
          </p>
        </div>

        {/* Comparison Table */}
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-0 mb-20"
          style={fadeIn('150ms')}
        >
          {/* Column Headers */}
          <div
            style={{
              background: '#F5F5F5',
              border: '1px solid rgba(20,60,60,0.1)',
              borderRight: '0',
              padding: '24px 32px',
              borderRadius: '8px 0 0 0',
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center rounded-full flex-shrink-0"
                style={{ width: '32px', height: '32px', background: 'rgba(20,60,60,0.06)' }}
              >
                <i className="ri-user-line" style={{ color: 'rgba(20,60,60,0.4)', fontSize: '16px' }} />
              </div>
              <span
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '13px',
                  color: 'rgba(20,60,60,0.5)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                On Your Own
              </span>
            </div>
          </div>

          <div
            style={{
              background: '#143C3C',
              border: '1px solid rgba(100,180,180,0.3)',
              padding: '24px 32px',
              borderRadius: '0 8px 0 0',
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center rounded-full flex-shrink-0"
                style={{ width: '32px', height: '32px', background: 'rgba(220,80,20,0.2)' }}
              >
                <i className="ri-building-4-line" style={{ color: '#DC5014', fontSize: '16px' }} />
              </div>
              <span
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '13px',
                  color: '#64B4B4',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                With Barkfy Hub
              </span>
            </div>
          </div>

          {/* Rows */}
          {comparisonRows.map((row, i) => (
            <>
              {/* Alone side */}
              <div
                key={`alone-${i}`}
                style={{
                  background: i % 2 === 0 ? '#FAFAFA' : '#F5F5F5',
                  border: '1px solid rgba(20,60,60,0.07)',
                  borderTop: '0',
                  borderRight: '0',
                  padding: '20px 32px',
                  borderRadius: i === comparisonRows.length - 1 ? '0 0 0 8px' : '0',
                }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="flex-shrink-0 flex items-center justify-center rounded-full mt-0.5"
                    style={{ width: '20px', height: '20px', background: 'rgba(20,60,60,0.06)' }}
                  >
                    <i className="ri-close-line" style={{ color: 'rgba(20,60,60,0.3)', fontSize: '12px' }} />
                  </div>
                  <p
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 400,
                      fontSize: '14px',
                      color: 'rgba(20,60,60,0.45)',
                      lineHeight: 1.6,
                    }}
                  >
                    {row.alone}
                  </p>
                </div>
              </div>

              {/* Hub side */}
              <div
                key={`hub-${i}`}
                style={{
                  background: i % 2 === 0 ? 'rgba(20,60,60,0.6)' : 'rgba(20,60,60,0.4)',
                  border: '1px solid rgba(100,180,180,0.15)',
                  borderTop: '0',
                  padding: '20px 32px',
                  borderRadius: i === comparisonRows.length - 1 ? '0 0 8px 0' : '0',
                }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="flex-shrink-0 flex items-center justify-center rounded-full mt-0.5"
                    style={{ width: '20px', height: '20px', background: '#DC5014' }}
                  >
                    <i className="ri-check-line" style={{ color: '#FFFFFF', fontSize: '12px' }} />
                  </div>
                  <p
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#F0F0F0',
                      lineHeight: 1.6,
                    }}
                  >
                    {row.hub}
                  </p>
                </div>
              </div>
            </>
          ))}
        </div>

        {/* CTA Block */}
        <div
          className="text-center"
          style={fadeIn('300ms')}
        >
          <div
            style={{
              background: 'rgba(20,60,60,0.04)',
              border: '1px solid rgba(20,60,60,0.12)',
              borderRadius: '12px',
              padding: '64px 48px',
              maxWidth: '680px',
              margin: '0 auto',
            }}
          >
            <div
              className="flex items-center justify-center mx-auto mb-6"
              style={{ width: '56px', height: '56px' }}
            >
              <i className="ri-shield-star-line" style={{ color: '#DC5014', fontSize: '44px' }} />
            </div>

            <h3
              style={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 700,
                fontSize: '28px',
                color: '#143C3C',
                marginBottom: '16px',
                lineHeight: 1.2,
              }}
            >
              Ready to operate like a corporation?
            </h3>

            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 300,
                fontSize: '15px',
                color: 'rgba(20,60,60,0.6)',
                lineHeight: 1.8,
                marginBottom: '36px',
                maxWidth: '480px',
                margin: '0 auto 36px',
              }}
            >
              Join the elite Hub and get immediate access to the $30M infrastructure, physical showroom, bank financing, and authority branding.
            </p>

            <button
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="font-sans font-bold whitespace-nowrap cursor-pointer transition-all duration-300 hover:brightness-110 active:scale-95 block w-full"
              style={{
                background: '#DC5014',
                color: '#FFFFFF',
                padding: '20px 48px',
                borderRadius: '6px',
                fontSize: '14px',
                letterSpacing: '0.12em',
                border: 'none',
                maxWidth: '420px',
                margin: '0 auto',
              }}
            >
              APPLY TO JOIN THE ELITE HUB
            </button>

            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontStyle: 'italic',
                fontSize: '11px',
                color: 'rgba(20,60,60,0.4)',
                marginTop: '16px',
                letterSpacing: '0.04em',
              }}
            >
              Limited availability: Only 3 spots left this quarter.
            </p>
          </div>
        </div>

      </div>
    </section>
  );
}
